/**
 * Cliente HTTP.
 *
 * A API mora na mesma origem, em `/api` — o subdomínio já diz qual barbearia é,
 * então não há token para carregar nem cabeçalho de tenant para lembrar.
 *
 * Erros viram `ApiError` com a mensagem em pt-BR que a API mandou. Nenhuma tela
 * inventa texto de erro: quem sabe o que aconteceu é o servidor.
 */

import type { ApiErrorBody } from '@barbearia/shared';

const BASE = import.meta.env.VITE_API_URL ?? '/api';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fields: Record<string, string[]>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.error.code;
    this.fields = body.error.fields ?? {};
  }

  /** Primeira mensagem de um campo, para colar embaixo do input. */
  fieldError(name: string): string | undefined {
    return this.fields[name]?.[0];
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, query?: Query): string {
  const url = `${BASE}${path}`;
  if (!query) return url;

  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }

  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function handle<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = text ? JSON.parse(text) : {};

  if (!response.ok) {
    throw new ApiError(response.status, body as ApiErrorBody);
  }

  return body as T;
}

async function send<T>(method: string, path: string, body?: unknown, query?: Query): Promise<T> {
  const response = await fetch(buildUrl(path, query), {
    method,
    headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
    // O cookie de sessão é httpOnly; sem isto ele não acompanha a requisição.
    credentials: 'same-origin',
  });

  return handle<T>(response);
}

export const api = {
  get: <T>(path: string, query?: Query) => send<T>('GET', path, undefined, query),
  post: <T>(path: string, body?: unknown, query?: Query) => send<T>('POST', path, body, query),
  put: <T>(path: string, body?: unknown, query?: Query) => send<T>('PUT', path, body, query),
  patch: <T>(path: string, body?: unknown, query?: Query) => send<T>('PATCH', path, body, query),
  delete: <T>(path: string, query?: Query) => send<T>('DELETE', path, undefined, query),
};

/** Canal de eventos da agenda. Devolve a função que encerra a escuta. */
export function subscribeToAgenda(onEvent: (type: string) => void): () => void {
  const source = new EventSource(`${BASE}/events/agenda`, { withCredentials: true });

  for (const type of ['APPOINTMENT_REQUESTED', 'APPOINTMENT_CHANGED', 'APPOINTMENT_CANCELED']) {
    source.addEventListener(type, () => onEvent(type));
  }

  return () => source.close();
}
